import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';

import { useAppSelector } from '@core/store/hooks';
import { Spinner } from '@core/components/ui';
import { formatRelativeTime } from '@core/i18n/format';
import { selectCurrentUser } from '@features/auth';
import { selectCurrentProject } from '@features/projects';
import { useGetConversationsQuery, selectCurrentConversationUuid } from '@features/ai';

function relativeTime(value, t, locale) {
  if (!value) return t('workspace.conversations.recently');
  const seconds = Math.round((new Date(value).getTime() - Date.now()) / 1000);
  if (Math.abs(seconds) < 60) return t('workspace.conversations.justNow');
  return formatRelativeTime(value, locale);
}

export function ConversationList() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const currentProject = useAppSelector(selectCurrentProject);
  const conversationUuid = useAppSelector(selectCurrentConversationUuid);
  const currentUser = useAppSelector(selectCurrentUser);
  const [query, setQuery] = useState('');

  const projectUuid = currentProject?.uuid || undefined;
  // `currentData` keeps the previous project's conversations from showing
  // under the new project's header, where clicking one navigates to a shortId
  // that never matches once the correct list lands.
  const { currentData: conversations = [], isFetching } = useGetConversationsQuery(projectUuid, { skip: !projectUuid });

  const handleLoad = useCallback((shortId) => {
    navigate(`/chat/${shortId}`);
  }, [navigate]);

  const filteredConversations = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return conversations;
    const untitled = t('workspace.conversations.untitled');
    return conversations.filter((conversation) => (conversation.first_message || untitled).toLowerCase().includes(normalized));
  }, [conversations, query, t]);

  return (
    <div className="flex flex-col gap-1">

      {conversations.length >= 8 && (
        <label className="relative mb-1 block">
          <span className="sr-only">{t('workspace.conversations.search')}</span>
          <Icon icon="mdi:magnify" className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-[var(--color-text-muted)]" />
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder={t('workspace.conversations.search')} className="w-full rounded-lg border border-[var(--color-border)] bg-white py-2 pl-8 pr-3 text-xs text-[var(--color-text)] outline-none focus:border-[var(--color-accent)] focus:ring-2 focus:ring-[var(--color-accent-subtle)]" />
        </label>
      )}

      {isFetching && conversations.length === 0 ? (
        <div className="flex items-center justify-center py-4">
          <Spinner size="sm" />
        </div>
      ) : conversations.length === 0 ? (
        <div className="px-3 py-5 text-center">
          <Icon icon="mdi:message-text-outline" className="mx-auto size-5 text-[var(--color-text-muted)]" />
          <p className="mt-2 text-xs font-medium text-[var(--color-text-secondary)]">{t('workspace.conversations.emptyTitle')}</p>
          <p className="mt-1 text-[10px] leading-4 text-[var(--color-text-muted)]">{t('workspace.conversations.emptyBody')}</p>
        </div>
      ) : filteredConversations.length === 0 ? (
        <p className="px-3 py-5 text-center text-xs text-[var(--color-text-muted)]">{t('workspace.conversations.noMatch')}</p>
      ) : (
        <div className="space-y-1">
          {filteredConversations.map((conv) => (
            <button
              key={conv.uuid}
              type="button"
              onClick={() => handleLoad(conv.short_id)}
              className={`group cursor-pointer flex w-full items-start gap-2 rounded-lg border px-2.5 py-2 text-left transition-colors ${
                conv.uuid === conversationUuid
                  ? 'border-[var(--color-accent)]/20 bg-[var(--color-accent-subtle)] text-[var(--color-accent)]'
                  : 'border-transparent text-[var(--color-text)] hover:border-[var(--color-border)] hover:bg-[var(--color-bg-muted)]'
              }`}
            >
              <Icon icon="mdi:message-text-outline" className="mt-0.5 size-4 shrink-0 opacity-70" />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-xs font-medium leading-5">{conv.first_message || t('workspace.conversations.untitled')}</span>
                <span className="block text-[10px] text-[var(--color-text-muted)]">{relativeTime(conv.updated_at, t, i18n.resolvedLanguage)} · {t('workspace.conversations.messageCount', { count: conv.message_count || 0 })}
                  {conv.owner_email !== currentUser?.email && ` · ${conv.owner_email}`}
                </span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
